<!--
  BirthdatePicker — the Month-select + Day-select + Year-input laid out as a
  single FULL-WIDTH flex row (month 1.3, day 1, year 1), with comfortable,
  usable widths. Replaces the per-view .cl-birthdate / .birthdate-row markup so
  both the Person and Client modals share one layout definition.

  INPUT LAYOUT ONLY. It owns no validation and no required-ness: each view keeps
  its own rule (Person requires month+day together with an optional year; Client
  treats all three as optional) in its own save handler. The three v-models are
  passthrough — the component emits exactly what the native controls hold, so a
  parent can keep binding with `.number`/null (Person) or number|''/string
  (Client) without this component changing its stored types.

  v-model:month, v-model:day, v-model:year.
-->
<template>
  <div class="birthdate">
    <select class="bd-month" :value="month" @change="emitMonth">
      <option :value="null">Month</option>
      <option v-for="(m, mi) in MONTHS" :key="mi" :value="mi + 1">{{ m }}</option>
    </select>
    <select class="bd-day" :value="day" @change="emitDay">
      <option :value="null">Day</option>
      <option v-for="d in 31" :key="d" :value="d">{{ d }}</option>
    </select>
    <input
      class="bd-year"
      :value="year"
      type="number"
      min="1900"
      max="2100"
      inputmode="numeric"
      placeholder="Year"
      @input="emitYear"
    />
  </div>
</template>

<script setup lang="ts">
type Val = number | string | null

defineProps<{
  month: Val
  day: Val
  year: Val
}>()

const emit = defineEmits<{
  (e: 'update:month', v: number | null): void
  (e: 'update:day', v: number | null): void
  (e: 'update:year', v: number | null): void
}>()

// Native <select> values arrive as strings; coerce the two required-pair fields
// to number|null so the "" placeholder option becomes null (matches both views'
// expectations). The year stays number|null too — Person stores number|null and
// Client coerces at save (/^\d{4}$/) so a numeric model value is compatible.
const toNum = (raw: string): number | null => {
  if (raw === '' || raw === 'null') return null
  const n = Number(raw)
  return Number.isNaN(n) ? null : n
}

function emitMonth(e: Event) { emit('update:month', toNum((e.target as HTMLSelectElement).value)) }
function emitDay(e: Event) { emit('update:day', toNum((e.target as HTMLSelectElement).value)) }
function emitYear(e: Event) { emit('update:year', toNum((e.target as HTMLInputElement).value)) }

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
</script>

<style scoped>
/* Full-width row: month a touch wider, day + year equal. */
.birthdate { display: flex; gap: 0.5rem; width: 100%; }
.birthdate select,
.birthdate input {
  padding: 0.55rem 0.5rem; border: 1px solid var(--border-default);
  border-radius: var(--radius-sm); background: var(--bg-input);
  color: var(--text-primary); font-size: 0.9rem;
}
.birthdate select:focus,
.birthdate input:focus { outline: none; border-color: var(--border-focus); }
.bd-month { flex: 1.3; min-width: 0; cursor: pointer; }
.bd-day   { flex: 1; min-width: 0; cursor: pointer; }
.bd-year  { flex: 1; min-width: 0; }
</style>
